import { useCallback, useId, useRef, useState } from 'react';

/**
 * Explicação flutuante de um campo — de onde o número vem e o que significa.
 *
 * Duas decisões que fazem diferença:
 *
 * - O balão usa `position: fixed` com coordenadas calculadas na hora. Dentro de
 *   uma tabela com `overflow-x: auto`, um balão absoluto seria recortado pelo
 *   container e apareceria pela metade.
 * - Abre no hover **e** no foco por teclado, e fecha com Esc — quem navega sem
 *   mouse também precisa da explicação.
 */
export default function Ajuda({ titulo, children, origem, rotulo = 'O que é este campo?' }) {
  const [aberto, setAberto] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const gatilho = useRef(null);
  const id = useId();

  const posicionar = useCallback(() => {
    const el = gatilho.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const largura = 300;
    const margem = 12;

    // Mantém o balão dentro da janela nas duas pontas.
    let left = r.left + r.width / 2 - largura / 2;
    left = Math.max(margem, Math.min(left, window.innerWidth - largura - margem));

    // Abre para baixo; se não houver espaço, abre para cima.
    const abaixo = window.innerHeight - r.bottom > 180;
    const top = abaixo ? r.bottom + 8 : r.top - 8;

    setPos({ top, left, largura, acima: !abaixo });
  }, []);

  const abrir = useCallback(() => {
    posicionar();
    setAberto(true);
  }, [posicionar]);

  return (
    <span className="ff-ajuda">
      <button
        ref={gatilho}
        type="button"
        className="ff-ajuda__gatilho"
        aria-label={rotulo}
        aria-describedby={aberto ? id : undefined}
        aria-expanded={aberto}
        onMouseEnter={abrir}
        onMouseLeave={() => setAberto(false)}
        onFocus={abrir}
        onBlur={() => setAberto(false)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setAberto(false);
          // O gatilho vive dentro de cabeçalhos ordenáveis: não disparar a ordenação.
          if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
        }}
        onClick={(e) => {
          e.stopPropagation();
          setAberto((v) => !v);
        }}
      >
        <i className="bi bi-question-circle" aria-hidden="true" />
      </button>

      {aberto && (
        <span
          id={id}
          role="tooltip"
          className={`ff-ajuda__balao${pos.acima ? ' acima' : ''}`}
          style={{ top: pos.top, left: pos.left, width: pos.largura }}
        >
          {titulo && <strong className="ff-ajuda__titulo">{titulo}</strong>}
          <span className="ff-ajuda__texto">{children}</span>
          {origem && (
            <span className="ff-ajuda__origem">
              <span className="ff-ajuda__origem-rotulo">Origem do dado</span>
              <code>{origem}</code>
            </span>
          )}
        </span>
      )}
    </span>
  );
}
